// src/lib/peer.js
// Production-safe PeerJS + WebCrypto E2EE
// Backward Compatible + RSA Wrapped-Key Enterprise Ready

import Peer from 'peerjs';

let peer = null;

/* =====================================================
   🔐 IN-MEMORY RSA KEY CACHE (NEW - SAFE ADDITION)
===================================================== */

let rsaPublicKeyMemory = null;
let rsaPrivateKeyMemory = null;

export function setRSAIdentityKeys(publicKey, privateKey) {
  rsaPublicKeyMemory = publicKey;
  rsaPrivateKeyMemory = privateKey;
}

export function getRSAPublicKey() {
  return rsaPublicKeyMemory;
}

export function getRSAPrivateKey() {
  return rsaPrivateKeyMemory;
}

/* =====================================================
   PEER CREATION (FIXED PATH ISSUE)
===================================================== */

export async function createPeer(myPeerId, options = {}) {
  if (peer) {
    try { peer.disconnect(); peer.destroy(); } catch {}
    peer = null;
  }

  const isLocal = window.location.hostname === 'localhost';
  const isSecure = window.location.protocol === 'https:';

  const defaultOpts = {
    host: isLocal ? 'localhost' : 'api.myfavv.live',
    port: isLocal
      ? 9000
      : (isSecure ? 443 : 9000),

    path: '/',
    secure: isSecure,

    config: {
      iceServers: [
        { urls: 'stun:api.myfavv.live:3478' },
        {
          urls: 'turn:api.myfavv.live:3478',
          username: 'myfavvuser',
          credential: 'SuperSecurePassword123',
        },
        { urls: 'stun:stun.l.google.com:19302' },
      ],
    },

    debug: 2,
    ...options
  };

  peer = new Peer(myPeerId, defaultOpts);

  return new Promise((resolve, reject) => {

    peer.on('open', id => {
      console.log('✅ Peer connected successfully:', id);
      resolve(peer);
    });

    peer.on('error', err => {
      console.error('❌ Peer error:', err);
      reject(err);
    });

    peer.on('disconnected', () => {
      console.warn('⚠️ Peer disconnected');
    });

    peer.on('close', () => {
      console.warn('⚠️ Peer closed');
    });
  });
}

export function getPeer() {
  return peer;
}

/* =====================================================
   🔐 RSA-OAEP IDENTITY (UNCHANGED)
===================================================== */

export async function generateRSAIdentityKeyPair() {
  return window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256'
    },
    true,
    ['wrapKey', 'unwrapKey']
  );
}

export async function exportRSAPublicKey(publicKey) {
  const spki = await window.crypto.subtle.exportKey('spki', publicKey);
  return arrayBufferToBase64(spki);
}

export async function importRSAPublicKey(base64) {
  const spki = base64ToArrayBuffer(base64);

  return window.crypto.subtle.importKey(
    'spki',
    spki,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    true,
    ['wrapKey']
  );
}

export async function exportRSAPrivateKey(privateKey) {
  const pkcs8 = await window.crypto.subtle.exportKey('pkcs8', privateKey);
  return arrayBufferToBase64(pkcs8);
}

export async function importRSAPrivateKey(base64) {
  const pkcs8 = base64ToArrayBuffer(base64);

  return window.crypto.subtle.importKey(
    'pkcs8',
    pkcs8,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['unwrapKey']
  );
}

/* =====================================================
   ECDH KEY AGREEMENT (UNCHANGED)
===================================================== */

export async function generateECDHKeyPair() {
  const kp = await window.crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey']
  );

  const pub = await window.crypto.subtle.exportKey('raw', kp.publicKey);

  return {
    keyPair: kp,
    publicKey: arrayBufferToBase64(pub)
  };
}

export async function importECDHPublic(base64Raw) {
  const raw = base64ToArrayBuffer(base64Raw);

  return window.crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    []
  );
}

/* =====================================================
   🔐 ECDH WRAPPING KEY (UNCHANGED)
===================================================== */

export async function deriveWrappingKey(privateKey, remotePublicRawBase64) {
  const remotePub = await importECDHPublic(remotePublicRawBase64);

  return window.crypto.subtle.deriveKey(
    { name: 'ECDH', public: remotePub },
    privateKey,
    { name: 'AES-KW', length: 256 },
    false,
    ['wrapKey', 'unwrapKey']
  );
}

/* =====================================================
   LEGACY AES DERIVATION (UNCHANGED)
===================================================== */

export async function deriveAESGCMKey(privateKey, remotePublicRawBase64) {
  const remotePub = await importECDHPublic(remotePublicRawBase64);

  return window.crypto.subtle.deriveKey(
    { name: 'ECDH', public: remotePub },
    privateKey,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

/* =====================================================
   🔐 SESSION AES (UNCHANGED)
===================================================== */

export async function generateSessionAESKey() {
  return window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

/* =====================================================
   🔐 RSA WRAP / UNWRAP SESSION AES (UNCHANGED)
===================================================== */

export async function wrapAESKeyWithRSA(aesKey, rsaPublicKey) {
  const wrapped = await window.crypto.subtle.wrapKey(
    'raw',
    aesKey,
    rsaPublicKey,
    { name: 'RSA-OAEP' }
  );
  return arrayBufferToBase64(wrapped);
}

export async function unwrapAESKeyWithRSA(wrappedBase64, rsaPrivateKey) {
  return window.crypto.subtle.unwrapKey(
    'raw',
    base64ToArrayBuffer(wrappedBase64),
    rsaPrivateKey,
    { name: 'RSA-OAEP' },
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export const wrapAESKey = wrapAESKeyWithRSA;
export const unwrapAESKey = unwrapAESKeyWithRSA;

/* =====================================================
   BASE64 HELPERS + AES (UNCHANGED)
===================================================== */

export function arrayBufferToBase64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

export function base64ToArrayBuffer(b64) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    arr[i] = bin.charCodeAt(i);
  }
  return arr.buffer;
}

export async function aesGcmEncrypt(aesKey, plaintext) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder().encode(plaintext);

  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    enc
  );

  return {
    ciphertext: arrayBufferToBase64(ct),
    iv: arrayBufferToBase64(iv.buffer)
  };
}

export async function aesGcmDecrypt(aesKey, ciphertextBase64, ivBase64) {
  const ct = base64ToArrayBuffer(ciphertextBase64);
  const iv = base64ToArrayBuffer(ivBase64);

  const plainBuf = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    aesKey,
    ct
  );

  return new TextDecoder().decode(plainBuf);
}